import { useState } from 'react';
import { Composite, Product, ProductCreate, ProductUpdate } from '../types/costwand.d'; // Added Product, ProductCreate, ProductUpdate
import { useComposites } from '../hooks/useComposites.ts';
import { useSurchargeLookup } from '../hooks/useSurchargeLookup.ts';
import CompositeForm from '../components/CompositeForm.tsx';
import CompositesTable from '../components/CompositesTable.tsx';
import ProductForm from '../components/ProductForm.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../components/ui/dialog.tsx';
import { useFamilyLookup } from '../hooks/useFamilyLookup.ts';
import { useSubFamilyLookup } from '../hooks/useSubFamilyLookup.ts';
import { useBarcodeType1Lookup } from '../hooks/useBarcodeType1Lookup.ts';
import { useBarcodeType2Lookup } from '../hooks/useBarcodeType2Lookup.ts';
import { useVendorLookup } from '../hooks/useVendorLookup.ts';
import { useCurrencyLookup } from '../hooks/useCurrencyLookup.ts';
import { useUnitOfMeasureLookup } from '../hooks/useUnitOfMeasureLookup.ts';
import { Spinner } from '../components/ui/spinner.tsx';
import { useProduct } from '../hooks/useProduct.ts'; // NEW: Import useProduct
import { useNavigate } from 'react-router-dom'; // NEW: Import useNavigate
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx';

const CompositesPage = () => {
  const {
    composites,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    updateCompositeDetails,
    deleteComposite,
    fetchComposites,
  } = useComposites();

  const { createProduct } = useProduct(); // NEW: Get createProduct from useProduct hook
  const navigate = useNavigate(); // NEW: Initialize useNavigate

  const { vendors, loading: vendorsLoading, error: vendorsError } = useVendorLookup();
  const { currencies, loading: currenciesLoading, error: currenciesError } = useCurrencyLookup();
  const { unitsOfMeasure, loading: unitsOfMeasureLoading, error: unitsOfMeasureError } = useUnitOfMeasureLookup();
  const { surcharges, loading: surchargesLoading, error: surchargesError } = useSurchargeLookup();
  const { families, loading: familiesLoading, error: familiesError } = useFamilyLookup();
  const { subFamilies, loading: subFamiliesLoading, error: subFamiliesError } = useSubFamilyLookup();
  const { barcodeType1s, loading: barcodeType1sLoading, error: barcodeType1sError } = useBarcodeType1Lookup();
  const { barcodeType2s, loading: barcodeType2sLoading, error: barcodeType2sError } = useBarcodeType2Lookup();

  const { whitelabelConfig } = useWhitelabelContext();

  // Combine all lookup loading states
  const allLookupsLoading =
    surchargesLoading ||
    familiesLoading ||
    subFamiliesLoading ||
    barcodeType1sLoading ||
    barcodeType2sLoading ||
    vendorsLoading || // Include vendor/currency/UOM for the ProductForm
    currenciesLoading ||
    unitsOfMeasureLoading;

  // Combine all lookup errors
  const allLookupsError =
    surchargesError ||
    familiesError ||
    subFamiliesError ||
    barcodeType1sError ||
    barcodeType2sError ||
    vendorsError ||
    currenciesError ||
    unitsOfMeasureError;

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false); // State for the EDIT CompositeForm
  const [editingComposite, setEditingComposite] = useState<Composite | null>(null); // State for the EDIT CompositeForm
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false); // State for the EDIT CompositeForm

  const [isCreateFormOpen, setIsCreateFormOpen] = useState<boolean>(false); // State for the ADD ProductForm (for composites)
  const [isCreateFormSubmitting, setCreateFormSubmitting] = useState<boolean>(false); // State for the ADD ProductForm (for composites)
  // editingProduct is not needed here as we are always creating new, initialData={null} will suffice for ProductForm

  const handleEditComposite = (composite: Composite) => {
    setEditingComposite(composite);
    setIsFormOpen(true);
  };

  const handleDeleteComposite = async (id: number) => {
    await deleteComposite(id);
    fetchComposites(1);
  };

  // Handler for submitting the EDIT CompositeForm
  const handleFormSubmit = async (data: {
    description: string;
    family_id: number;
    subfamily_id: number | null;
    barcode_1_id: number | null;
    barcode_2_id: number | null;
    tax_profile_id: number;
    markup_perc_wholesale: number;
    markup_perc_retail: number;
  }) => {
    setFormSubmitting(true);

    try {
      let success = false;
      if (editingComposite) {
        success = await updateCompositeDetails(editingComposite.id!, data);
      }

      if (success) {
        setIsFormOpen(false);
      }
    } catch (e: any) {
      // Error handling is managed by the global error context
    } finally {
      setFormSubmitting(false);
    }
  };

  // NEW: Handler for submitting the ADD ProductForm (for composite creation)
  const handleCreateFormSubmit = async (data: ProductCreate | ProductUpdate, isComposite: boolean): Promise<boolean | number> => {
    setCreateFormSubmitting(true);

    try {
      // When creating a new composite, we always treat it as a ProductCreate
      const createData: ProductCreate = {
        ...data as ProductCreate,
        created_by: 'tester',
      };

      const newProductId = await createProduct(createData); // Use createProduct from useProduct hook

      if (isComposite && typeof newProductId === 'number') {
        setIsCreateFormOpen(false); // Close the create form on success
        navigate(`/composition-edit/${newProductId}`); // Redirect to composition edit page
        return newProductId;
      } else if (typeof newProductId === 'boolean' && newProductId === true) {
        // This branch would handle standard product creation if the switch was off.
        // For this page, the primary intent is composite creation.
        setIsCreateFormOpen(false);
        return newProductId;
      } else {
        return false; // Creation failed
      }
    } catch (e: any) {
      // Global error handler will catch this.
      return false; // Indicate failure
    } finally {
      setCreateFormSubmitting(false);
    }
  };


  const getEntityTitle = (defaultTitle: string, singular: boolean) => 
    (singular && whitelabelConfig.entities?.['composites'].title_singular) || whitelabelConfig.entities?.['composites'].title || defaultTitle;

  // Cancel handler for the EDIT CompositeForm
  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingComposite(null);
  };

  // NEW: Function to open the ADD ProductForm dialog
  const handleAddCompositeClick = () => {
    setIsCreateFormOpen(true);
  };

  // NEW: Cancel handler for the ADD ProductForm
  const handleCreateFormCancel = () => {
    setIsCreateFormOpen(false);
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">{getEntityTitle('Composite Product')} Management</h2>
        <Dialog open={isCreateFormOpen} onOpenChange={setIsCreateFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddCompositeClick} disabled={allLookupsLoading}> {/* Use new click handler */}
              {allLookupsLoading ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" /> Loading form...
                </span>
              ) : (
                'Add New ' + getEntityTitle('Composite', true) // NEW: Button text
              )}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{'Add New Composite Product'}</DialogTitle> {/* NEW: Dialog title */}
              <DialogDescription>
                Create a new composite product record. {/* NEW: Dialog description */}
              </DialogDescription>
            </DialogHeader>
            {allLookupsError && (
              <p className="text-destructive text-sm p-2 bg-red-100 border border-red-400 rounded">
                Error loading lookup data: {allLookupsError}
              </p>
            )}
            <ProductForm
              initialData={null} // Always null for new creation
              onSubmit={handleCreateFormSubmit}
              onCancel={handleCreateFormCancel} // NEW: Use the new cancel handler
              isSubmitting={isCreateFormSubmitting}
              vendors={vendors}
              families={families}
              subFamilies={subFamilies}
              barcodeType1s={barcodeType1s}
              barcodeType2s={barcodeType2s}
              currencies={currencies}
              unitsOfMeasure={unitsOfMeasure}
              lookupLoading={allLookupsLoading}
              createComposite={true} // NEW: Pre-select the composite switch
              entityId="composites"
            />
          </DialogContent>
        </Dialog>
      </div>

      <CompositesTable
        composites={composites}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onEdit={handleEditComposite}
        onDelete={handleDeleteComposite}
        loading={loading}
        error={error}
        baseEndpoint="/composites" // NEW: Pass the base endpoint
        entityId="composites"
      />

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Composite Product Details</DialogTitle>
            <DialogDescription>
              Update the details, categorization, barcode types, surcharge profile, and markup percentages for this composite product.
            </DialogDescription>
          </DialogHeader>
          {allLookupsError && (
            <p className="text-destructive text-sm p-2 bg-red-100 border border-red-400 rounded">
              Error loading lookup data: {allLookupsError}
            </p>
          )}
          {editingComposite && (
            <CompositeForm
              initialData={editingComposite}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={formSubmitting}
              surcharges={surcharges}
              families={families}
              subFamilies={subFamilies}
              barcodeType1s={barcodeType1s}
              barcodeType2s={barcodeType2s}
              lookupLoading={allLookupsLoading}
              entityId="composites"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompositesPage;